import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref(JSON.parse(localStorage.getItem("saa_user")) || null);
  const token = ref(localStorage.getItem("saa_token") || null);
  const isAuthenticating = ref(false);

  // Getters
  const loggedIn = computed(() => !!token.value && !!user.value);
  const currentUser = computed(() => user.value);

  // Actions
  async function login(authToken) {
    isAuthenticating.value = true;
    token.value = authToken;
    localStorage.setItem("saa_token", authToken);

    try {
      // withCredentials:false because the token is in the query param, not a cookie.
      // Chrome enforces strict CORS for credentialed requests; this avoids that check.
      const { data } = await axios.get("/auth", {
        params: { token: authToken },
        withCredentials: false,
      });
      if (data?.logged_in) {
        user.value = data.user;
        localStorage.setItem("saa_user", JSON.stringify(data.user));
        return { success: true };
      }
      return { success: false, error: data?.message || "Invalid credentials" };
    } catch (error) {
      // Distinguish between network/CORS failures and actual auth errors
      const msg =
        error.response?.data?.message ||
        (error.message === "Network Error"
          ? "Network / CORS error — check API URL and CORS config"
          : error.message) ||
        "Login failed";
      console.error("Login failed:", msg, error);
      return { success: false, error: msg };
    } finally {
      isAuthenticating.value = false;
      if (!user.value) {
        logout();
      }
    }
  }

  async function validate() {
    isAuthenticating.value = true;

    // Check if saa_token cookie exists
    const hasCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("saa_token="));

    if (!hasCookie) {
      isAuthenticating.value = false;
      logout();
      return null;
    }

    if (!token.value) {
      isAuthenticating.value = false;
      return null;
    }

    try {
      const { data } = await axios.get("/auth");
      if (data?.logged_in) {
        user.value = data.user;
        localStorage.setItem("saa_user", JSON.stringify(data.user));
        return data.user;
      }
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      isAuthenticating.value = false;
    }

    return null;
  }

  async function directLogin(username, password) {
    isAuthenticating.value = true;
    try {
      const { data } = await axios.post(
        "/auth/login",
        { username, password },
        { withCredentials: false }
      );

      if (data?.success && data?.token && data?.user) {
        token.value = data.token;
        user.value = data.user;
        localStorage.setItem("saa_token", data.token);
        localStorage.setItem("saa_user", JSON.stringify(data.user));
        return { success: true, user: data.user };
      }
      return { success: false, error: data?.message || "Invalid credentials" };
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Gagal masuk. Periksa username dan password.";
      return { success: false, error: msg };
    } finally {
      isAuthenticating.value = false;
    }
  }

  function logout() {
    user.value = null;
    token.value = null;
    localStorage.removeItem("saa_token");
    localStorage.removeItem("saa_user");
    // In dev mode (VUE_APP_DEV_TOKEN set), skip redirect so auto-login can recover
    if (process.env.VUE_APP_REDIRECT_URL && !process.env.VUE_APP_DEV_TOKEN) {
      window.location.href = process.env.VUE_APP_REDIRECT_URL;
    }
  }

  return {
    // State
    user,
    token,
    isAuthenticating,
    // Getters
    loggedIn,
    currentUser,
    // Actions
    login,
    directLogin,
    validate,
    logout,
  };
});

